import type { AppRole } from '@/config/constants';
import type { Membership } from '@/types';

export interface ActiveOrganizationRow {
  id: string;
  is_active: boolean;
}

export interface ActiveTenantRow {
  id: string;
  organization_id: string;
  is_active: boolean;
  created_at: string;
}

export interface MembershipState {
  memberships: Membership[];
  activeOrganizationId: string | null;
  activeTenantId: string | null;
  activeRole: AppRole | null;
}

export function resolveMembershipState(
  memberships: Membership[],
  organizations: ActiveOrganizationRow[],
  activeTenants: ActiveTenantRow[],
): MembershipState {
  const orderedActiveTenants = [...activeTenants].sort((left, right) => {
    if (left.organization_id !== right.organization_id) {
      return left.organization_id.localeCompare(right.organization_id);
    }

    if (left.created_at !== right.created_at) {
      return left.created_at.localeCompare(right.created_at);
    }

    return left.id.localeCompare(right.id);
  });
  const activeOrganizationIds = new Set(
    organizations
      .filter((organization) => organization.is_active)
      .map((organization) => organization.id),
  );
  const activeTenantIds = new Set(orderedActiveTenants.map((tenant) => tenant.id));

  const filteredMemberships = memberships.filter((membership) => {
    if (!activeOrganizationIds.has(membership.organization_id)) {
      return false;
    }

    if (membership.is_org_wide) {
      return true;
    }

    return activeTenantIds.has(membership.tenant_id);
  });

  const primaryMembership = filteredMemberships[0] ?? null;
  const primaryActiveTenant = primaryMembership
    ? orderedActiveTenants.find((tenant) => {
        if (tenant.organization_id !== primaryMembership.organization_id) {
          return false;
        }

        return primaryMembership.is_org_wide || tenant.id === primaryMembership.tenant_id;
      }) ?? null
    : null;

  return {
    memberships: filteredMemberships,
    activeOrganizationId: primaryMembership?.organization_id ?? null,
    activeTenantId: primaryActiveTenant?.id ?? null,
    activeRole: primaryMembership?.role ?? null,
  };
}

/**
 * Resolve which membership applies when the user switches their active tenant.
 * Prefers a direct tenant-scoped membership; falls back to an org-wide membership
 * in the same organization so org admins can switch freely between tenants.
 */
export function resolveActiveTenantMembership(
  memberships: Membership[],
  tenantId: string,
  activeOrganizationId: string | null,
): Membership | null {
  return memberships.find((item) => item.tenant_id === tenantId)
    ?? memberships.find(
      (item) => item.organization_id === activeOrganizationId && item.is_org_wide,
    )
    ?? null;
}
